import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  PrepaidCreditAddForm,
  PrepaidCreditAddInitialValues,
  PrepaidCreditComponents,
  PrepaidCreditAdditionalParams,
  PrepaidCreditFormOptions,
  onPrepaidCreditFormSaved,
  IPrepaidCreditFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreatePrepaidCredit, PermissionEnum, } from 'atlas-shared';
import { createPrepaidCredit, fetchPrepaidCredit, getPrepaidCreditCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreatePrepaidCredit> {}

const PrepaidCreditFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreatePrepaidCredit>>();

  const param_props: IPrepaidCreditFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => PrepaidCreditAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => PrepaidCreditFormOptions(values || PrepaidCreditAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreatePrepaidCredit, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreatePrepaidCredit) => save(() => createPrepaidCredit(values).then(
    res => onSaved ? onSaved(res, navigate) : onPrepaidCreditFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchPrepaidCredit(+id)
        .then(({
          organization_id,
          currency,
          amount,
          valid_from,
          valid_until,
        }) => {
          setCloned({
            organization_id,
            currency,
            amount,
            valid_from,
            valid_until,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.PrepaidCredit, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={PrepaidCreditComponents}
        additionalParams={PrepaidCreditAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...PrepaidCreditAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('PREPAID_CREDIT')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PrepaidCreditFormAddView = React.memo(withAdd(PrepaidCreditFormAdd, getPrepaidCreditCreateSchema, PrepaidCreditAddInitialValues));
